import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const Stars = ({ value, onChange, size = 16 }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type={onChange ? "button" : undefined}
        onClick={onChange ? () => onChange(n) : undefined}
        className={onChange ? "cursor-pointer" : "cursor-default"}
        data-testid={onChange ? `rating-star-${n}` : undefined}
      >
        <Star
          size={size}
          strokeWidth={1.5}
          className={n <= value ? "fill-champagne text-champagne" : "text-stone-300"}
        />
      </button>
    ))}
  </div>
);

const Reviews = () => {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, title: "", body: "", website: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/reviews", form);
      toast.success(t("reviews.form.success"));
      setForm({ name: "", rating: 5, title: "", body: "", website: "" });
      setOpen(false);
      load();
    } catch {
      toast.error("Error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reviews-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-champagne mb-4">Kaós®</p>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-charcoal">{t("reviews.title")}</h1>
            <p className="text-stone-500 mt-4">{t("reviews.subtitle")}</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="write-review-button"
                className="rounded-none bg-charcoal text-white hover:bg-stone-800 h-12 px-8 text-xs uppercase tracking-[0.2em]"
              >
                {t("reviews.write")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-bone rounded-none border-stone-200">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-charcoal">{t("reviews.write")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-5 mt-2">
                <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" />
                <Input
                  required
                  placeholder={t("reviews.form.name")}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="review-name"
                  className="rounded-none border-stone-300 bg-white focus-visible:ring-1 focus-visible:ring-charcoal"
                />
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">{t("reviews.form.rating")}</p>
                  <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={22} />
                </div>
                <Input
                  required
                  placeholder={t("reviews.form.title")}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="review-title"
                  className="rounded-none border-stone-300 bg-white focus-visible:ring-1 focus-visible:ring-charcoal"
                />
                <Textarea
                  required
                  rows={4}
                  placeholder={t("reviews.form.body")}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  data-testid="review-body"
                  className="rounded-none border-stone-300 bg-white focus-visible:ring-1 focus-visible:ring-charcoal"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="review-submit"
                  className="w-full rounded-none bg-charcoal text-white hover:bg-stone-800 h-12 text-xs uppercase tracking-[0.2em]"
                >
                  {t("reviews.form.submit")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.map((r, i) => (
          <Reveal key={i} delay={(i % 3) * 100}>
            <div
              data-testid={`review-card-${i}`}
              className="bg-white border border-stone-200 p-8 h-full flex flex-col"
            >
              <Stars value={r.rating} />
              <h3 className="font-serif text-xl text-charcoal mt-5">{r.title}</h3>
              <p className="text-stone-600 leading-relaxed mt-3 flex-1">{r.body}</p>
              <p className="text-xs uppercase tracking-[0.18em] text-stone-400 mt-6">— {r.name}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

export default Reviews;
